import React from 'react'
import AntdAlert from 'antd/es/alert'
import type { AlertProps } from 'antd/es/alert'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import { Alert as ShadcnSeamAlert } from './Alert.shadcn'

/**
 * Compat seam leaf for antd's `antd/es/alert` — one of the six presentational
 * components put behind the design toggle in the same PR (Input, Card, Alert,
 * Divider, Empty, Skeleton), after `./Button` (#970), `./Typography` +
 * `./Space` (#971) and `./Tag` (#972).
 *
 * A LEAF, not the `src/antd` barrel, on purpose (PRO-130): the barrel's
 * `export * from 'antd'` pulls antd/es/index.js — the whole component barrel —
 * into whatever chunk imports it. `update-design` ships a real seam at this
 * same path with the same shape — a NAMED `Alert` and no default — so a
 * redirected call site needs no further change when that lands.
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch this component renders antd's own
 * Alert with the props it was given: same element, same class names, same DOM.
 * The only difference from what the barrel handed back before — antd's own
 * component — is one extra component in the React tree, which renders nothing
 * of its own. `./__tests__/alert-seam-parity.test.tsx` asserts that byte for
 * byte across a prop matrix, and it is the test to keep green: `development`
 * continuing to ship antd unchanged to customers is the whole premise of
 * shipping shadcn one component at a time.
 *
 * antd's one static, `Alert.ErrorBoundary`, is NOT carried across, and that is
 * measured rather than assumed: no file in this repo names it (this app has
 * its own `src/ui/ErrorBoundary`). Adding an undispatched passthrough would be
 * a surface nothing exercises; adding a dispatched one would mean porting a
 * component whose job is to render a stack trace. If a call site ever appears,
 * it goes here beside the root.
 * ------------------------------------------------------------------------- */
const AlertDispatcher = (props: AlertProps) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamAlert {...props} />

  return <AntdAlert {...props} />
}

AlertDispatcher.displayName = 'Alert'

export const Alert = AlertDispatcher
